@if (tours.isLoading()) {
  <div class="loading-grid">
    @for (item of [1, 2, 3]; track item) {
      <p-skeleton
        height="160px"
        width="100%" />
    }
  </div>
} @else if (tours.hasError()) {
  <!-- Error State -->
  <div class="tw:border tw:border-surface tw:rounded-sm tw:mt-3 tw:p-3">
    <cor-no-data
      [showIcon]="true"
      noDataMessage="">
      <div class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:mt-3">
        <div class="tw:font-semibold tw:text-3xl">Unable to Load Tours</div>
        <div class="tw:mb-3 tw:text-xl">{{ tours.error() }}</div>
        <p-button
          test-id="learning-hub-try-again"
          [buttonProps]="{ autofocus: false }"
          label="Try Again"
          icon="pi pi-refresh"
          (onClick)="refreshTours()" />
      </div>
    </cor-no-data>
  </div>
} @else if (tours.tracks().length === 0) {
  <div class="tw:border tw:border-surface tw:rounded-sm tw:mt-3 tw:p-3">
    <cor-no-data
      [showIcon]="true"
      noDataMessage="">
      <div class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:mt-3">
        <div class="tw:font-semibold tw:text-2xl">No Tours Available</div>
        <div class="tw:text-lg">There are currently no tours configured in the Help Center</div>
      </div>
    </cor-no-data>
  </div>
} @else {
  <div class="two">
    <aside class="side">
      <label class="search">
        <i class="pi pi-search"></i>
        <input
          type="search"
          placeholder="Search tours"
          test-id="learning-hub-search"
          [value]="query()"
          (input)="query.set($any($event.target).value)" />
      </label>
      <div class="side-label">Tracks</div>
      @for (track of tours.tracks(); track track.name; let i = $index) {
        <button
          type="button"
          class="nav"
          [class.on]="!query() && !showWebinars() && activeTrack() === track"
          [class.finished]="isFinished(track)"
          [attr.test-id]="'tour-group-' + track.name"
          (click)="selectTrack(track)">
          <span
            class="ring"
            [style.background]="ringStyle(track, i)">
            <i>
              @if (isFinished(track)) {
                <em class="pi pi-check"></em>
              }
            </i>
          </span>
          <span class="nav-text">
            <span class="nav-title">{{ track.name }}</span>
            <span class="nav-meta">
              @if (isFinished(track)) {
                All {{ track.available }} done
              } @else {
                {{ track.done }} of {{ track.available }}
                @if (minutesLeft(track)) {
                  · {{ minutesLeft(track) }} min left
                }
              }
            </span>
          </span>
        </button>
      }
      <button
        type="button"
        class="nav"
        [class.on]="!query() && showWebinars()"
        test-id="learning-hub-webinars"
        (click)="selectWebinars()">
        <span class="ring plain">
          <i><em class="pi pi-video"></em></i>
        </span>
        <span class="nav-text">
          <span class="nav-title">Webinars</span>
          <span class="nav-meta">
            @if (webinars.isLoading()) {
              Loading
            } @else {
              {{ webinars.webinarList().length }} recordings
            }
          </span>
        </span>
      </button>
      @if (tours.progress().available > 0) {
        <div
          class="side-sum"
          test-id="learning-hub-progress">
          <div class="side-sum-n">
            {{ tours.progress().done }} of {{ tours.progress().available }}
          </div>
          <div class="side-sum-t">tours done across all tracks</div>
          <div class="hub-bar">
            <i [style.width.%]="(tours.progress().done / tours.progress().available) * 100"></i>
          </div>
        </div>
      }
      <div class="side-foot">
        <a
          class="side-link"
          href="https://docs.genguardx.ai"
          target="_blank"
          rel="noopener">
          <i class="pi pi-book"></i>
          Documentation
          <i class="pi pi-external-link tw:ms-auto"></i>
        </a>
      </div>
    </aside>

    <section
      class="pane"
      [style.--track-accent]="trackColour(showWebinars() ? undefined : activeTrack())">
      @if (query()) {
        <h3 class="pane-title">Search results</h3>
        <p class="pane-blurb">{{ searchResults().length }} tours match "{{ query() }}"</p>
        @if (searchResults().length === 0) {
          <p class="pane-empty">Nothing matches. Try a shorter word, or clear the search.</p>
        }
        @for (row of searchResults(); track row.title) {
          <ng-container *ngTemplateOutlet="lesson; context: { $implicit: row, showTrack: true }" />
        }
      } @else if (showWebinars()) {
        <h3 class="pane-title">Webinars</h3>
        <p class="pane-blurb">
          Recorded sessions from the GGX team. Watch one, or download its slides.
        </p>
        @if (webinars.isLoading()) {
          <div class="loading-grid">
            @for (item of [1, 2, 3, 4, 5, 6]; track item) {
              <p-skeleton
                height="200px"
                width="100%" />
            }
          </div>
        } @else if (webinars.webinarList().length === 0) {
          <!-- Empty State -->
          <div class="tw:border tw:border-surface tw:rounded-sm tw:p-3">
            <cor-no-data
              [showIcon]="true"
              noDataMessage="">
              <div class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:mt-3">
                <div class="tw:font-semibold tw:text-2xl">No Webinars Available</div>
                <div class="tw:text-lg">
                  There are currently no webinars configured in the Help Center
                </div>
              </div>
            </cor-no-data>
          </div>
        } @else {
          <div class="wgrid">
            @for (webinar of webinars.webinarList(); track webinar.id; let index = $index) {
              <div
                class="wcard"
                [attr.role]="webinar.link ? 'button' : null"
                [attr.test-id]="'webinar-' + webinar.title"
                [attr.title]="webinar.description"
                (click)="webinar.link ? openWebinarLink(webinar) : null">
                <div class="wposter">
                  @if (webinars.thumbnails()[webinar.id]; as thumbnail) {
                    <img
                      class="wthumb"
                      alt=""
                      [src]="thumbnail" />
                  }
                  <span class="wplay"><i class="pi pi-play"></i></span>
                  <span class="wdur">{{ webinar.duration + ' min' }}</span>
                  <div class="wacts">
                    <p-button
                      test-id="learning-hub-watch"
                      [buttonProps]="{ autofocus: false }"
                      icon="pi pi-play-circle"
                      label="Watch"
                      position="left"
                      severity="success"
                      variant="outlined"
                      [rounded]="true"
                      (onClick)="openWebinarLink(webinar); $event.stopPropagation()" />

                    @if (webinar.fileId) {
                      <p-button
                        test-id="learning-hub-pdf"
                        [buttonProps]="{ autofocus: false }"
                        icon="pi pi-download"
                        label="PDF"
                        position="left"
                        severity="secondary"
                        variant="outlined"
                        [rounded]="true"
                        (onClick)="downloadWebinarPDF(webinar); $event.stopPropagation()" />
                    }
                  </div>
                </div>

                <div class="wbody">
                  <div class="wtitle">{{ webinar.title }}</div>
                  <div class="wcode">{{ 'WEBINAR-' + (index + 1) }}</div>
                </div>
              </div>
            }
          </div>
        }
      } @else if (activeTrack(); as track) {
        <h3 class="pane-title">{{ track.name }}</h3>
        @if (trackBlurb(track)) {
          <p class="pane-blurb">{{ trackBlurb(track) }}</p>
        }
        <div class="tprog">
          <div class="hub-bar">
            <i [style.width.%]="track.available ? (track.done / track.available) * 100 : 0"></i>
          </div>
          <span>
            {{ track.done }} of {{ track.available }} done
            @if (minutesLeft(track)) {
              · {{ minutesLeft(track) }} min left
            }
          </span>
        </div>

        @if (tours.upNext(); as next) {
          <div
            class="banner"
            test-id="learning-hub-up-next">
            <span class="banner-icon"><i class="pi pi-play"></i></span>
            <div class="banner-text">
              <div class="banner-kicker">Up next</div>
              <div class="banner-title">{{ next.titleName }}</div>
              <div class="banner-chips">
                @if (next.minutes) {
                  <span class="chip">{{ next.minutes }} min</span>
                }
                @if (next.steps) {
                  <span class="chip">{{ next.steps }} steps</span>
                }
                @if (next.group && next.group.split('.')[0] !== track.name) {
                  <span class="chip">{{ next.group.split('.')[0] }}</span>
                }
              </div>
            </div>
            <p-button
              class="ink"
              [buttonProps]="{ autofocus: false }"
              icon="pi pi-arrow-right"
              iconPos="right"
              label="Continue"
              test-id="learning-hub-continue"
              (onClick)="startTour(next)" />
          </div>
        }

        @for (row of track.rows; track row.title) {
          @if (row.subHeading) {
            <div class="sub">
              <span>{{ row.sub }}</span>
              <span class="sub-line"></span>
              <span class="sub-meta">
                {{ subStats(track, row.sub).done }} of {{ subStats(track, row.sub).total }} done
              </span>
            </div>
          }
          <ng-container *ngTemplateOutlet="lesson; context: { $implicit: row, showTrack: false }" />
        }
      }
    </section>
  </div>
}

<ng-template
  #lesson
  let-row
  let-showTrack="showTrack">
  <div
    class="lesson"
    [class.soon]="row.state === 'soon'"
    [class.done]="row.state === 'done'"
    [class.next]="row === tours.upNext()"
    [attr.role]="row.key ? 'button' : null"
    [attr.tabindex]="row.key ? 0 : null"
    [attr.test-id]="'tour-' + row.title"
    (click)="row.key ? startTour(row) : null"
    (keydown.enter)="row.key ? startTour(row) : null">
    <span
      class="lesson-num"
      [class.todo]="row.state !== 'done' && row !== tours.upNext()"
      [class.now]="row.state !== 'done' && row === tours.upNext()">
      @if (row.state === 'done') {
        <i class="pi pi-check"></i>
      }
    </span>
    <div class="lesson-text">
      <div class="lesson-title">
        @if (row.titleNum) {
          <span class="lesson-code">{{ row.titleNum }}</span>
        }
        {{ row.titleName }}
        @if (row.tag) {
          <span class="lesson-tag">{{ row.tag }}</span>
        }
        @if (showTrack && row.group) {
          <span class="lesson-tag">{{ row.group.split('.')[0] }}</span>
        }
      </div>
      @if (row.description) {
        <div class="lesson-desc">{{ row.description }}</div>
      }
    </div>
    <div class="lesson-right">
      @if (row.state === 'soon') {
        <span class="lesson-meta">Coming soon</span>
      } @else if (row.minutes || row.steps) {
        <span class="lesson-meta">
          @if (row.minutes) {
            {{ row.minutes }} min
          }
          @if (row.minutes && row.steps) {
            ·
          }
          @if (row.steps) {
            {{ row.steps }} steps
          }
        </span>
      }
      @if (row.key && row === tours.upNext() && !showTrack) {
        <p-button
          class="ink"
          [buttonProps]="{ autofocus: false }"
          label="Continue"
          [attr.test-id]="'tour-start-' + row.title"
          (onClick)="startTour(row); $event.stopPropagation()" />
      } @else if (row.key) {
        <p-button
          [buttonProps]="{ autofocus: false }"
          [label]="row.state === 'done' ? 'Replay' : 'Start'"
          severity="secondary"
          variant="outlined"
          [pTooltip]="
            row.state === 'done' ? 'You have finished this tour. Click to go through it again' : ''
          "
          [attr.test-id]="'tour-start-' + row.title"
          (onClick)="startTour(row); $event.stopPropagation()" />
      } @else {
        <p-button
          test-id="learning-hub-coming-soon-this-tour-unlocks-in-a-later"
          [buttonProps]="{ autofocus: false }"
          icon="pi pi-lock"
          [disabled]="true"
          severity="secondary"
          variant="outlined"
          pTooltip="Coming soon. This tour unlocks in a later week of the sandbox" />
      }
    </div>
  </div>
</ng-template>
